import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, Clock, ArrowLeft, Tag, Share2, Heart, MessageSquare, ChevronRight, Twitter, Facebook, Linkedin, Link2 } from 'lucide-react';
import { getBlogPost, getBlogPosts, likeBlogPost } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

function BlogPostPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const res = await getBlogPost(postId);
      setPost(res.data);
      
      // Fetch related posts
      const relatedRes = await getBlogPosts({ category: res.data.category, limit: 3 });
      setRelatedPosts(relatedRes.data.posts?.filter(p => p.id !== postId).slice(0, 3) || []);
    } catch (error) {
      console.error('Error fetching post:', error);
      // Show demo post
      setPost(getDemoPost());
      setRelatedPosts(getDemoRelatedPosts());
    } finally {
      setLoading(false);
    }
  };

  const getDemoPost = () => ({
    id: postId,
    title: 'Welcome to Supremics: Your Ultimate Web Comic Destination',
    excerpt: 'Discover the world of web comics like never before.',
    cover_image: 'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=1200',
    category: 'announcements',
    author: 'Supremics Team',
    author_avatar: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    read_time: 5,
    likes: 42,
    views: 1250,
    tags: ['welcome', 'guide', 'getting-started'],
    content: `
# Welcome to Supremics!

We're thrilled to have you here. Supremics is more than just a web comic platform – it's a community of passionate creators and readers who share a love for visual storytelling.

## What Makes Supremics Special?

### For Readers

- **Vast Library**: Access thousands of web comics across multiple genres
- **Personalized Experience**: Our smart recommendations help you discover comics you'll love
- **Support Creators**: Directly support your favorite artists through our donation system

### For Creators

- **Powerful Tools**: Our AI-powered tools help you colorize, translate, and create stunning covers
- **Analytics Dashboard**: Understand your audience with detailed analytics
- **Monetization**: Earn from subscriptions, donations, and weekly prizes

## Getting Started

1. **Create an Account**: Sign up for free to start reading
2. **Explore**: Browse our extensive library of comics
3. **Subscribe**: Upgrade to Creator or Contributor for premium features
4. **Connect**: Follow your favorite creators and join the community

## Our Mission

At Supremics, we believe that great stories should be accessible to everyone. We're committed to building a platform that empowers creators and delights readers.

## What's Next?

Stay tuned for exciting updates, including:

- New AI features for creators
- Enhanced mobile reading experience
- Community events and challenges
- Creator collaboration tools

Thank you for being part of our journey. We can't wait to see what stories you'll discover – or create!

*- The Supremics Team*
    `
  });

  const getDemoRelatedPosts = () => [
    {
      id: '2',
      title: 'Creator Spotlight: MangaMaster\'s Journey',
      cover_image: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=400',
      category: 'creator-spotlight',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      read_time: 8
    },
    {
      id: '3',
      title: 'How to Use AI Tools to Enhance Your Comics',
      cover_image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400',
      category: 'tutorials',
      created_at: new Date(Date.now() - 172800000).toISOString(),
      read_time: 12
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to like posts');
      return;
    }
    try {
      await likeBlogPost(postId);
      setLiked(!liked);
      setPost(prev => ({
        ...prev,
        likes: liked ? prev.likes - 1 : prev.likes + 1
      }));
    } catch (error) {
      setLiked(!liked);
      setPost(prev => ({
        ...prev,
        likes: liked ? prev.likes - 1 : prev.likes + 1
      }));
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || 'Supremics Blog';
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      copy: null
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-24" data-testid="blog-post-loading">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-32 bg-[var(--border)] rounded" />
            <div className="h-12 w-3/4 bg-[var(--border)] rounded" />
            <div className="h-[400px] bg-[var(--border)] rounded-2xl" />
            <div className="space-y-3">
              <div className="h-4 bg-[var(--border)] rounded" />
              <div className="h-4 bg-[var(--border)] rounded" />
              <div className="h-4 w-2/3 bg-[var(--border)] rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-post-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-teko mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[var(--primary)] hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="blog-post-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img 
          src={post.cover_image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-transparent to-black/30" />
        
        {/* Back Button */}
        <Link 
          to="/blog"
          className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors backdrop-blur-sm"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={18} />
          Back to Blog
        </Link>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 -mt-20 relative z-10">
        {/* Header */}
        <header className="bg-[var(--surface)] rounded-2xl p-6 md:p-10 border border-[var(--border)] mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-[var(--primary)] text-white text-sm font-medium rounded-full">
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
              <Clock size={14} />
              {post.read_time} min read
            </span>
          </div>
          
          <h1 className="text-3xl md:text-5xl font-teko text-[var(--text)] mb-6">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E63946] to-[#FF6B35] flex items-center justify-center text-white font-bold">
                {post.author?.charAt(0)}
              </div>
              <div>
                <p className="font-medium text-[var(--text)]">{post.author}</p>
                <p className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                  <Calendar size={12} />
                  {formatDate(post.created_at)}
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all
                  ${liked 
                    ? 'bg-[var(--primary)] border-[var(--primary)] text-white' 
                    : 'border-[var(--border)] hover:border-[var(--primary)]'}`}
                data-testid="like-post"
              >
                <Heart size={18} fill={liked ? 'currentColor' : 'none'} />
                {post.likes}
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border)] hover:border-[var(--primary)] transition-all"
                  data-testid="share-post"
                >
                  <Share2 size={18} />
                  Share
                </button>
                
                {showShareMenu && (
                  <div className="absolute right-0 top-full mt-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg p-2 min-w-[150px] z-50">
                    <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--background)] rounded text-sm">
                      <Twitter size={16} />
                      Twitter
                    </button>
                    <button onClick={() => handleShare('facebook')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--background)] rounded text-sm">
                      <Facebook size={16} />
                      Facebook
                    </button>
                    <button onClick={() => handleShare('linkedin')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--background)] rounded text-sm">
                      <Linkedin size={16} />
                      LinkedIn
                    </button>
                    <hr className="my-1 border-[var(--border)]" />
                    <button onClick={() => handleShare('copy')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--background)] rounded text-sm">
                      <Link2 size={16} />
                      Copy Link
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none 
          prose-headings:font-teko prose-headings:text-[var(--text)]
          prose-p:text-[var(--text-secondary)]
          prose-a:text-[var(--primary)]
          prose-strong:text-[var(--text)]
          prose-ul:text-[var(--text-secondary)]
          prose-ol:text-[var(--text-secondary)]
          prose-blockquote:border-[var(--primary)]
          prose-code:text-[var(--primary)]
          prose-pre:bg-[var(--surface)]
          mb-12"
        >
          <div dangerouslySetInnerHTML={{ __html: formatContent(post.content) }} />
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-12">
            {post.tags.map(tag => (
              <span 
                key={tag}
                className="flex items-center gap-1 px-3 py-1 bg-[var(--surface)] border border-[var(--border)] rounded-full text-sm text-[var(--text-secondary)]"
              >
                <Tag size={12} />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="border-t border-[var(--border)] pt-12">
            <h2 className="text-3xl font-teko mb-6">Related Posts</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map(relPost => (
                <Link
                  key={relPost.id}
                  to={`/blog/${relPost.id}`}
                  className="group flex gap-4 bg-[var(--surface)] rounded-xl p-4 border border-[var(--border)] hover:border-[var(--primary)]/50 transition-all"
                  data-testid={`related-post-${relPost.id}`}
                >
                  <img 
                    src={relPost.cover_image}
                    alt={relPost.title}
                    className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-[var(--primary)] font-medium uppercase">
                      {relPost.category}
                    </span>
                    <h3 className="font-teko text-lg text-[var(--text)] group-hover:text-[var(--primary)] transition-colors line-clamp-2 mt-1">
                      {relPost.title}
                    </h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-2 flex items-center gap-2">
                      <Clock size={10} />
                      {relPost.read_time} min read
                    </p>
                  </div>
                  <ChevronRight className="text-[var(--text-secondary)] group-hover:text-[var(--primary)] transition-colors self-center" size={20} />
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}

// Simple markdown-like content formatter
function formatContent(content) {
  if (!content) return '';
  
  return content
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    // Numbered lists
    .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
    // Paragraphs
    .split('\n\n').map(p => {
      if (p.startsWith('<h') || p.startsWith('<ul') || p.startsWith('<ol') || p.startsWith('<li')) {
        return p;
      }
      return `<p>${p}</p>`;
    }).join('\n');
}

export default BlogPostPage;
